import {
    Box,
    Flex,
    GridItem,
    SimpleGrid,
    Skeleton,
    Text,
    VStack,
    HStack,
    useColorModeValue,
    Badge,
    Icon,
} from "@chakra-ui/react";
import { WiRaindrop } from "react-icons/wi";
import Gradient from "javascript-color-gradient";

function RankingRain({ stations }) {
    let firstTable = stations && stations.length ? stations.slice(0, 5) : [];
    let secondTable = stations && stations.length ? stations.slice(5, 10) : [];

    const borderColor = useColorModeValue("gray.200", "gray.700");

    return (
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
            <GridItem>
                <StationList rows={firstTable} startIndex={0} />
            </GridItem>
            <GridItem borderLeft={{ lg: "1px solid" }} borderColor={borderColor} pl={{ lg: 4 }}>
                <StationList rows={secondTable} startIndex={5} />
            </GridItem>
        </SimpleGrid>
    );
}

function StationList({ rows, startIndex }) {
    const textPrimary = useColorModeValue("gray.800", "white");
    const rowBg = useColorModeValue("gray.50", "gray.750");
    const defaultRankBg = useColorModeValue("gray.200", "gray.600");

    function getRainColor(rain) {
        if (isNaN(rain)) return "#90CDF4";
        rain = Math.round(rain);

        if (rain <= 0) rain = 1;
        if (rain >= 50) rain = 50;

        return new Gradient()
            .setColorGradient("#90CDF4", "#4299E1", "#2B6CB0")
            .setMidpoint(50)
            .getColor(parseInt(rain));
    }

    // Rain row component
    const RainRow = ({ station, index }) => (
        <Flex
            align="center"
            py={2}
            px={3}
            bg={(startIndex + index) % 2 === 0 ? "transparent" : rowBg}
            borderRadius="md"
            transition="all 0.15s"
            _hover={{ bg: rowBg }}
        >
            {/* Rank Number */}
            <Box
                w="24px"
                h="24px"
                borderRadius="full"
                bg={index < 3 ? getRainColor(station.rain) : defaultRankBg}
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                fontWeight="bold"
                mr={3}
                flexShrink={0}
            >
                {startIndex + index + 1}
            </Box>

            {/* Station Info */}
            <VStack spacing={0} align="start" flex={1} minW={0}>
                <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={textPrimary}
                    noOfLines={1}
                >
                    {station.city}/{station.state}
                </Text>
                <Badge
                    colorScheme="gray"
                    variant="subtle"
                    fontSize="2xs"
                    textTransform="lowercase"
                >
                    {station.provider}
                </Badge>
            </VStack>

            {/* Rain Amount */}
            <HStack
                bg={getRainColor(station.rain)}
                color="white"
                px={3}
                py={1}
                borderRadius="lg"
                fontWeight="bold"
                fontSize="sm"
                textShadow="0 1px 2px rgba(0,0,0,0.2)"
                flexShrink={0}
                spacing={1}
            >
                <Icon as={WiRaindrop} boxSize={4} />
                <Text>{station.rain}mm</Text>
            </HStack>
        </Flex>
    );

    // Loading skeleton
    const SkeletonRow = ({ index }) => (
        <Flex
            align="center"
            py={2}
            px={3}
            bg={index % 2 === 0 ? "transparent" : rowBg}
            borderRadius="md"
        >
            <Skeleton w="24px" h="24px" borderRadius="full" mr={3} />
            <VStack spacing={1} align="start" flex={1}>
                <Skeleton h="14px" w="70%" />
                <Skeleton h="10px" w="30%" />
            </VStack>
            <Skeleton w="70px" h="28px" borderRadius="lg" />
        </Flex>
    );

    return (
        <VStack spacing={1} align="stretch">
            {rows.length ? (
                rows.map((station, index) => (
                    <RainRow
                        key={station.identifier || index}
                        station={station}
                        index={index}
                    />
                ))
            ) : (
                [...Array(5).keys()].map(i => (
                    <SkeletonRow key={i} index={i} />
                ))
            )}
        </VStack>
    );
}

export default RankingRain;
